<template>
  <!-- Simplified Contextual Breadcrumb -->
  <nav class="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1 px-6 py-2 max-w-7xl mx-auto" aria-label="Breadcrumb">
    <!-- Projects: always a link -->
    <RouterLink
      to="/projects"
      class="hover:text-gray-700 dark:hover:text-gray-200 transition"
    >
      Projects
    </RouterLink>
    
    <svg class="w-4 h-4 mx-2 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
    </svg>
    
    <!-- Project/Entity Context -->
    <template v-if="entityName">
      <!-- Make entity name clickable to go back to session overview -->
      <RouterLink
        :to="{ path: '/projects/research', query: { projectId: projectId } }"
        class="text-gray-700 dark:text-gray-200 font-medium hover:text-blue-600 dark:hover:text-blue-400 transition"
      >
        {{ entityName }}
      </RouterLink>
      
      <template v-if="showPhaseContext">
        <svg class="w-4 h-4 mx-2 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
        
        <span class="text-gray-600 dark:text-gray-300">
          {{ currentPhase }}
        </span>
      </template>
    </template>
    
    <!-- Fallback for research workspace without entity -->
    <template v-else>
      <RouterLink
        :to="{ path: '/projects/research', query: { projectId: projectId } }"
        class="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition"
      >
        Research Workspace
      </RouterLink>
    </template>
  </nav>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { storeToRefs } from 'pinia'

const props = defineProps({
  sessionId: String,
  projectId: String
})

const route = useRoute()
const sessionStore = useSessionStore()
const { entityName } = storeToRefs(sessionStore)

// Get projectId from props or route
const projectId = computed(() => props.projectId || route.query.projectId)

// Determine the current phase based on route
const currentPhase = computed(() => {
  const path = route.path
  if (path.includes('/data-collection') || path.includes('/research/questions')) {
    return 'Data Collection'
  } else if (path.includes('/data-overview')) {
    return 'Data Review'
  } else if (path.includes('/risk-analysis') || path.includes('/analyze')) {
    return 'Risk Analysis'
  } else if (path.includes('/report')) {
    return 'Report'
  } else if (path.includes('/research') && route.query.sessionId) {
    return 'Entity Selection'
  }
  return 'Research'
})

// Only show phase context if we're deep in a specific workflow
const showPhaseContext = computed(() => {
  return entityName.value && (
    route.path.includes('/data-collection') || 
    route.path.includes('/data-overview') ||
    route.path.includes('/analyze') ||
    route.path.includes('/report')
  )
})
</script>